import { UIStatus } from "@storytell/streaming";
import { onCleanup } from "solid-js";
import { PromptBarMode, useAppUI } from "../../../layouts/minimal/AppUIContext";
import { usePromptBarContext } from "../PromptBarContext";
import { useFileUpload } from "../../../../lib/file-upload";
import { useWire } from "../../../../wire";

/**
 * Hook for managing drag and drop functionality
 * Enhanced to be more aggressive about dropzone activation in submitted/chat mode
 */
export const useDragAndDrop = () => {
  const { state, actions } = useAppUI();
  const context = usePromptBarContext();
  const wire = useWire();
  const scope = wire.chat.scope;

  const fileUpload = useFileUpload({
    getAuthToken: () => wire.auth.token(),
    onAssetCreated: (assetId, asset) => {
      wire.sync.assets.set(assetId, { ...asset, id: assetId });
      // Find and remove any temporary asset with the same filename
      const tempAsset = scope.uploadingAssets().find((a) => a.filename === asset.data.originalFilename);
      if (tempAsset) {
        scope.removeUploadingAsset(tempAsset.id);
      }

      wire.chat.scope.addAsset(assetId);
    },
  });

  const isDropzoneMode = () => state.promptBar.mode === PromptBarMode.DROPZONE;

  // Check if we're in submitted/chat mode (more aggressive dropzone activation)
  const isInSubmittedMode = () => {
    const streamingState = context.streaming;
    return streamingState?.uiStatus() === UIStatus.GENERATING || streamingState?.uiStatus() === UIStatus.DONE;
  };

  const handleWindowDragEnter = (e: DragEvent) => {
    e.preventDefault();
    // Only activate dropzone if files are being dragged
    if (e.dataTransfer?.types.includes("Files")) {
      actions.setPromptBarMode(PromptBarMode.DROPZONE);
    }
  };

  const handleWindowDragLeave = (e: DragEvent) => {
    e.preventDefault();
    // In submitted mode, be less aggressive about hiding dropzone
    const shouldHideDropzone = isInSubmittedMode()
      ? e.clientX === 0 && e.clientY === 0 // Only hide when truly leaving window
      : e.clientX === 0 && e.clientY === 0; // Same behavior as before

    if (shouldHideDropzone) {
      actions.setPromptBarMode(PromptBarMode.NORMAL);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    // Keep dropzone active while dragging over the prompt bar
    // In submitted mode, ensure dropzone stays active
    if (isInSubmittedMode() && e.dataTransfer?.types.includes("Files")) {
      actions.setPromptBarMode(PromptBarMode.DROPZONE);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    const currentTarget = e.currentTarget as HTMLElement;
    const relatedTarget = e.relatedTarget as Node;

    // In submitted mode, be less aggressive about hiding dropzone when leaving prompt bar area
    if (currentTarget && !currentTarget.contains(relatedTarget)) {
      if (!isInSubmittedMode()) {
        // In initial mode, hide dropzone when leaving prompt bar
        actions.setPromptBarMode(PromptBarMode.NORMAL);
      }
    }
  };

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []);

    if (files.length > 0) {
      // Switch to normal mode immediately to show prompt view with scope indicators
      actions.setPromptBarMode(PromptBarMode.NORMAL);

      // Create temporary asset entries for immediate UI feedback
      const tempAssets = files.map((file) => {
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        return { tempId, filename: file.name };
      });

      try {
        await fileUpload.uploadFiles(files, wire.auth.realm().projectId);

        // Clean up any remaining temporary assets (in case onAssetCreated didn't fire for some files)
        tempAssets.forEach(({ filename }) => {
          // Only remove if this temporary asset still exists and hasn't been replaced by a real asset
          const stillExists = scope.uploadingAssets().find((asset) => asset.filename === filename);
          if (stillExists) {
            scope.removeUploadingAsset(stillExists.id);
          }
        });
      } catch (error) {
        console.error("Failed to upload files:", error);
        // Update any remaining temporary assets to error state
        tempAssets.forEach(({ tempId }) => {
          const stillExists = scope.uploadingAssets().find((asset) => asset.id === tempId);
          if (stillExists) {
            scope.updateUploadingAssetStatus(tempId, "error");
          }
        });
      }
    } else {
      actions.setPromptBarMode(PromptBarMode.NORMAL);
    }
  };

  // Enhanced global drag detection for submitted mode
  const handleDocumentDragEnter = (e: DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer?.types.includes("Files")) {
      actions.setPromptBarMode(PromptBarMode.DROPZONE);
    }
  };

  const handleDocumentDragOver = (e: DragEvent) => {
    e.preventDefault();
    // Ensure dropzone stays active while dragging anywhere in submitted mode
    if (isInSubmittedMode() && e.dataTransfer?.types.includes("Files")) {
      actions.setPromptBarMode(PromptBarMode.DROPZONE);
    }
  };

  // Set up global drag listeners with enhanced coverage
  if (typeof window !== "undefined") {
    // Window-level listeners
    window.addEventListener("dragenter", handleWindowDragEnter);
    window.addEventListener("dragleave", handleWindowDragLeave);

    // Document-level listeners for better coverage
    document.addEventListener("dragenter", handleDocumentDragEnter);
    document.addEventListener("dragover", handleDocumentDragOver);

    onCleanup(() => {
      window.removeEventListener("dragenter", handleWindowDragEnter);
      window.removeEventListener("dragleave", handleWindowDragLeave);
      document.removeEventListener("dragenter", handleDocumentDragEnter);
      document.removeEventListener("dragover", handleDocumentDragOver);
    });
  }

  return {
    isDropzoneMode,
    isInSubmittedMode, // Export for debugging/testing
    handleDragOver,
    handleDragLeave,
    handleDrop,
    fileUpload, // Export file upload state for UI components
  };
};
